import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import NotificationEventTypes from "../../../constants/notificationEventsEnum";
import { useForm, useFieldArray } from "react-hook-form";
import { createPortal } from "react-dom";
import DropdownWithCustomValuesModal from "../../../components/DropdownWithCustomValuesModal";
const Siblings = ({ selectedStudent, parentCnic }) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const userId = selectedStudent.userId;
  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      userId: userId,
      siblingDtos: [
        {
          id: 0,
          name: "",
          gender: "",
          dob: null,
          isSchoolGoing: "",
          schoolName: "",
          class: "",
          mediumOfStudy: "",
        },
      ],
    },
  });

  const {
    fields: siblingDtos,
    append: appendOtherSiblingsField,
    remove: removeOtherSiblingsField,
    replace,
  } = useFieldArray({
    control,
    name: "siblingDtos",
  });

  const [pupils, setPupils] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPupils, setIsLoadingPupils] = useState(false);
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(false);
  const [existingSiblings, setExistingSiblings] = useState([]);
  const [isStudentsExpanded, setIsStudentsExpanded] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  useEffect(() => {
    const fetchPupils = async () => {
      if (!userId) {
        console.error("No valid userId available");
        return;
      }
      setIsLoadingPupils(false);
      try {
        const response = await get(
          `Student/GetPupilsInformationByUserId?userId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setPupils(response);
      } catch (error) {
        console.error("Error fetching pupils:", error);
        toast.error("Failed to fetch pupils.");
      } finally {
        setIsLoadingPupils(false);
      }
    };

    const fetchSiblings = async () => {
      if (!userId) {
        console.error("No valid userId available for siblings");
        return;
      }
      setIsLoadingSiblings(false);
      try {
        const response = await get(
          `Student/GetSiblingInformation?UserId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response && response.length > 0) {
          const formattedSiblings = response.map((sibling) => ({
            id: sibling.id || 0,
            name: sibling.name || "",
            gender: sibling.gender?.toString() || "",
            dob: sibling.dob ? sibling.dob.split("T")[0] : null,
            isSchoolGoing:
              sibling.isSchoolGoing === true
                ? "Yes"
                : sibling.isSchoolGoing === false
                ? "No"
                : "",
            schoolName: sibling.schoolName || "",
            class: sibling.class || "",
            mediumOfStudy: sibling.mediumOfStudy || "",
            parentId: sibling.parentId,
          }));
          replace(formattedSiblings);
        } else {
          replace([
            {
              id: 0,
              name: "",
              gender: "",
              dob: null,
              isSchoolGoing: "",
              schoolName: "",
              class: "",
              mediumOfStudy: "",
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching siblings:", error);
        toast.error("Failed to fetch sibling information.");
      } finally {
        setIsLoadingSiblings(false);
      }
    };

    const fetchExistingSiblings = async () => {
      if (!parentCnic) return;
      setIsLoadingExisting(true);
      try {
        const response = await get(
          `InspStudentFamilyRegistrations/GetAllStudentsByCnic?cnic=${parentCnic}&centerId=${centerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.isSuccess && response.familyWithStudentsDto?.students) {
          // Filter out the current student
          setExistingSiblings(
            response.familyWithStudentsDto.students.filter(
              (s) =>
                s.registrationId !== selectedStudent.studentFirstRegistrationId
            )
          );
        }
      } catch (error) {
        console.error("Error fetching existing siblings:", error);
      } finally {
        setIsLoadingExisting(false);
      }
    };

    fetchPupils();
    fetchSiblings();
    fetchExistingSiblings();
  }, [parentCnic, token, centerId]);

  const onSaveForm = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return false;
    }

    const isValid = await trigger("siblingDtos");
    if (!isValid) {
      // toast.error("Please fix validation errors before submitting.");
      return false;
    }

    const formValues = getValues();

    // Filter out completely empty rows
    const filledSiblings = formValues.siblingDtos.filter((sibling) => {
      return (
        sibling.name ||
        sibling.gender !== "" ||
        sibling.dob ||
        sibling.isSchoolGoing !== "" ||
        sibling.schoolName ||
        sibling.class ||
        sibling.mediumOfStudy
      );
    });

    const formData = {
      userId: userId || 0,
      siblingDtos: filledSiblings.map((sibling) => ({
        id: sibling.id || 0,
        name: sibling.name || "",
        gender:
          sibling.gender !== "" && sibling.gender !== undefined
            ? parseInt(sibling.gender)
            : null,
        dob: sibling.dob?.split("T")[0] || null,
        isSchoolGoing:
          sibling.isSchoolGoing === "Yes"
            ? true
            : sibling.isSchoolGoing === "No"
            ? false
            : null,
        schoolName: sibling.schoolName || "",
        class: sibling.class || "",
        mediumOfStudy: sibling.mediumOfStudy || "",
      })),
    };

    try {
      const response = await post("Student/AddSiblingInformation", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message);
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error submitting sibling information:", error);
      toast.error("Failed to save sibling information.");
      return false;
    }
  };

  return (
    <div className="profile-content-area">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Siblings</h5>
      </div>
      <div className="profile-scrollable-area p-1">
        {/* <h5 className="mb-2 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Added Pupils
        </h5> */}

        <div className="row d-none">
          {/* ... existing pupils code ... */}
        </div>

        {isLoadingExisting ? (
          <div className="text-center my-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          existingSiblings.length > 0 && (
            <div className="col-12 mb-4">
              <div
                className="card shadow-sm border-0"
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #eef2f7",
                }}
              >
                <div
                  className="card-header d-flex justify-content-between align-items-center py-3"
                  style={{
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #eef2f7",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        backgroundColor: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "12px",
                      }}
                    >
                      <i
                        className="fas fa-users text-white"
                        style={{ fontSize: "18px" }}
                      ></i>
                    </div>
                    <div>
                      <h5
                        className="mb-0 fw-semibold"
                        style={{ color: "#1e293b" }}
                      >
                        Registered Siblings
                      </h5>
                      <small className="text-muted">
                        {existingSiblings.length} student
                        {existingSiblings.length !== 1 ? "s" : ""} found
                      </small>
                    </div>
                  </div>
                  {existingSiblings.length > 5 && (
                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center"
                      onClick={() => setIsStudentsExpanded(!isStudentsExpanded)}
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      <i
                        className={`fas fa-${
                          isStudentsExpanded ? "chevron-up" : "chevron-down"
                        } me-2`}
                        style={{ fontSize: "11px" }}
                      ></i>
                      {isStudentsExpanded ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover m-0"
                      style={{ borderCollapse: "separate", borderSpacing: 0 }}
                    >
                      <thead style={{ backgroundColor: "#f8fafc" }}>
                        <tr>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Reg No
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Name
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            DOB
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Gender
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Class
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Session
                          </th>
                          {/* <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "center",
                            }}
                          >
                            Action
                          </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {(isStudentsExpanded
                          ? existingSiblings
                          : existingSiblings.slice(0, 5)
                        ).map((student, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#fafbfc",
                              borderBottom: "1px solid #eef2f7",
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              {index + 1}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 600,
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                style={{
                                  backgroundColor: "#f1f5f9",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontFamily: "monospace",
                                }}
                              >
                                {student.registrationId}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {student.studentFullName}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                color: "#64748b",
                                fontSize: "14px",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <i
                                  className="fas fa-calendar-alt me-2"
                                  style={{ fontSize: "12px", color: "#94a3b8" }}
                                ></i>
                                {new Date(student.dob).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  backgroundColor:
                                    student.gender === "Male"
                                      ? "#dbeafe"
                                      : "#fce7f3",
                                  color:
                                    student.gender === "Male"
                                      ? "#1d4ed8"
                                      : "#be185d",
                                }}
                              >
                                {student.gender}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              {student.className}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                color: "#64748b",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor: "#f0fdf4",
                                  color: "#166534",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {student.sessionName}
                              </span>
                            </td>
                            {/* <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                              }}
                            >
                              <button
                                className="btn btn-sm"
                                onClick={() =>
                                  toast.info(
                                    `Viewing ${student.studentFullName}`
                                  )
                                }
                                title="View Sibling"
                                style={{
                                  backgroundColor: "transparent",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "8px",
                                  padding: "6px 12px",
                                  color: "#3b82f6",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.borderColor = "#3b82f6";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.color = "#3b82f6";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                }}
                              >
                                <i
                                  className="fas fa-eye me-1"
                                  style={{ fontSize: "12px" }}
                                ></i>
                                View
                              </button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )
        )}


        <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Other Siblings (If any)
        </h5>

        {isLoadingSiblings ? (
          <div className="text-center">
            <span className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </span>
          </div>
        ) : (
          <>
            <div className="row g-2">
              <div className="col">
                <label className="form-label">Sibling Name</label>
              </div>
              <div className="col">
                <label className="form-label">Gender</label>
              </div>
              <div className="col">
                <label className="form-label">Date of Birth</label>
              </div>
              <div className="col">
                <label className="form-label">School Going</label>
              </div>
              <div className="col">
                <label className="form-label">School Name</label>
              </div>
              <div className="col">
                <label className="form-label">Class/Grade</label>
              </div>
              <div className="col">
                <label className="form-label">Medium of Study</label>
              </div>
              <div className="col-1">
                <label className="form-label">Action</label>
              </div>
            </div>

            {siblingDtos?.map((field, index) => (
              <div className="row g-2 mb-2" key={field.id}>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.name`, {
                      validate: (value, formValues) => {
                        const sibling = getValues(`siblingDtos.${index}`);
                        const hasOtherFieldsFilled =
                          sibling.gender !== "" ||
                          sibling.dob ||
                          sibling.isSchoolGoing !== "" ||
                          sibling.schoolName ||
                          sibling.class ||
                          sibling.mediumOfStudy;

                        if (hasOtherFieldsFilled && !value?.trim()) {
                          return "This field is required.";
                        }
                        return true;
                      },
                    })}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.name && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].name.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <select
                    {...register(`siblingDtos.${index}.gender`, {})}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value="1">Male</option>
                    <option value="0">Female</option>
                  </select>
                  {errors.siblingDtos?.[index]?.gender && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].gender.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="date"
                    {...register(`siblingDtos.${index}.dob`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.dob && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].dob.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <select
                    {...register(`siblingDtos.${index}.isSchoolGoing`, {})}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.siblingDtos?.[index]?.isSchoolGoing && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].isSchoolGoing.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.schoolName`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.schoolName && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].schoolName.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.class`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.class && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].class.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.mediumOfStudy`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.mediumOfStudy && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].mediumOfStudy.message}
                    </small>
                  )}
                </div>
                <div className="col-1">
                  <button
                    className="btn btn-outline-primary py-1 px-2 lh-2"
                    type="button"
                    onClick={() =>
                      appendOtherSiblingsField({
                        userId: userId,
                        siblingDtos: [
                          {
                            id: 0,
                            name: "",
                            gender: "",
                            dob: null,
                            isSchoolGoing: "",
                            schoolName: "",
                            class: "",
                            mediumOfStudy: "",
                          },
                        ],
                      })
                    }
                  >
                    <span className="fas fa-plus"></span>
                  </button>
                  {siblingDtos.length > 1 && (
                    <button
                      className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                      type="button"
                      onClick={() => removeOtherSiblingsField(index)}
                    >
                      <span className="fas fa-minus"></span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="mt-auto">
        <div className="d-flex justify-content-end gap-3">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onSaveForm}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default Siblings;
